import { useTranslation } from 'react-i18next';
import { useState, useMemo, useRef, useContext, useEffect } from 'react';
import { Button } from '../interactive/button.jsx';
import { Combobox } from './combobox.jsx';
import { Pill } from '../display/pill.jsx';
import { AppCtx } from '../../app-context.jsx';
import { buildFieldOptions, buildSelectOptions, createFilterHandlers, getChipDisplayValue, useFilterFocus, useFilterOpenEffect } from './filter-bar-helpers.jsx';
import { buildDateClauseValue, buildDatePresetOptions, datePresetNeedsPicker, parseDateClauseToState } from './date-filter-helpers.js';
import { buildAmountClauseValue, buildAmountOperatorOptions, parseAmountClauseToState } from './amount-filter-helpers.js';
import { getCurrencyRateSnapshot } from '../../../../shared-web/currency-rates.js';

// eslint-disable-next-line complexity
export function FilterBar({ filterFields, items, onFilterChange, open, onToggle, initialClauses, defaultCurrency, lockedCurrency }) {
	const { t, i18n } = useTranslation();
	const appCtx = useContext(AppCtx);
	const dateFormat = appCtx?.dateFormat ?? 'iso';
	const [clauses, setClauses] = useState(/** @type {Array<{ fieldKey: string, value: string }>} */ (initialClauses || []));
	const [selectedField, setSelectedField] = useState('');
	const [inputValue, setInputValue] = useState('');
	const [datePreset, setDatePreset] = useState('');
	const [dateStart, setDateStart] = useState('');
	const [dateEnd, setDateEnd] = useState('');
	const [amountOp, setAmountOp] = useState('');
	const [amount, setAmount] = useState('');
	const [amountEnd, setAmountEnd] = useState('');
	const [filterCurrency, setFilterCurrency] = useState('');
	const [editingIndex, setEditingIndex] = useState(/** @type {number | null} */ (null));
	const valueSelectRef = useRef(/** @type {HTMLInputElement | null} */ (null));
	const valuePickerRef = useRef(/** @type {{ open: () => void } | null} */ (null));
	const valueInputRef = useRef(null);
	const datePresetRef = useRef(/** @type {HTMLInputElement | null} */ (null));
	const datePresetPickerRef = useRef(/** @type {{ open: () => void } | null} */ (null));
	const dateStartRef = useRef(null);
	const dateEndRef = useRef(null);
	const amountOpRef = useRef(/** @type {HTMLInputElement | null} */ (null));
	const amountOpPickerRef = useRef(/** @type {{ open: () => void } | null} */ (null));
	const amountInputRef = useRef(null);
	useFilterOpenEffect(open, editingIndex, setSelectedField, setInputValue);
	useEffect(() => { if (open && editingIndex === null) { setDatePreset(''); setDateStart(''); setDateEnd(''); setAmountOp(''); setAmount(''); setAmountEnd(''); setFilterCurrency(''); } }, [open, editingIndex]);
	const fieldOptions = useMemo(() => buildFieldOptions(filterFields, t), [filterFields, t]);
	const currentField = useMemo(() => fieldOptions.find((f) => f.key === selectedField) ?? null, [fieldOptions, selectedField]);
	const selectOptions = useMemo(() => buildSelectOptions(currentField, filterFields, items, i18n.language), [currentField, filterFields, items, i18n.language]);
	const datePresetOptions = useMemo(() => buildDatePresetOptions(t), [t]);
	const amountOperatorOptions = useMemo(() => buildAmountOperatorOptions(t), [t]);
	const currencyOptions = useMemo(() => {
		const snap = getCurrencyRateSnapshot();
		return snap.currencies.map((c) => ({ value: c.code, label: c.code }));
	}, []);
	const isSelect = currentField?.type === 'select' && currentField.key !== '*';
	const isDate = currentField?.type === 'date' && currentField.key !== '*';
	const isCurrency = currentField?.type === 'currency' && currentField.key !== '*';
	const resolvedDefaultCurrency = defaultCurrency || getCurrencyRateSnapshot().baseCurrency || '';
	const effectiveCurrency = lockedCurrency || filterCurrency || resolvedDefaultCurrency;
	useFilterFocus({ open, selectedField, isSelect, valueSelectRef, valuePickerRef, valueInputRef });
	useEffect(() => {
		if (!open || !selectedField || !isDate) { return () => {}; }
		const isEditing = editingIndex !== null;
		const needs = datePresetNeedsPicker(datePreset);
		if (isEditing && needs === 1) { dateStartRef.current?.focus(); return () => {}; }
		if (isEditing && needs === 2) { dateStartRef.current?.focus(); return () => {}; }
		if (!isEditing && needs > 0) { dateStartRef.current?.focus(); return () => {}; }
		datePresetRef.current?.focus();
		const id = window.setTimeout(() => datePresetPickerRef.current?.open(), 0);
		return () => window.clearTimeout(id);
	}, [open, selectedField, isDate, editingIndex, datePreset]);
	useEffect(() => {
		if (!open || !selectedField || !isCurrency) { return () => {}; }
		if (editingIndex === null && !filterCurrency && lockedCurrency) { setFilterCurrency(lockedCurrency); }
		if (editingIndex === null && !filterCurrency && !lockedCurrency && resolvedDefaultCurrency) { setFilterCurrency(resolvedDefaultCurrency); }
		if (editingIndex !== null) { return () => {}; }
		if (!amountOp) {
			amountOpRef.current?.focus();
			const id = window.setTimeout(() => amountOpPickerRef.current?.open(), 0);
			return () => window.clearTimeout(id);
		}
		if (amountOp && amountOp !== 'between' && !amount) { amountInputRef.current?.focus(); return () => {}; }
		return () => {};
	}, [open, selectedField, isCurrency, editingIndex, amountOp, amount, filterCurrency, lockedCurrency, resolvedDefaultCurrency]);
	const base = createFilterHandlers({ clauses, setClauses, onFilterChange, setEditingIndex, setSelectedField, setInputValue, onToggle, selectedField, inputValue, editingIndex, fieldOptions });
	function onFieldChange(key) {
		base.handleFieldChange(key);
		setDatePreset(''); setDateStart(''); setDateEnd('');
		setAmountOp(''); setAmount(''); setAmountEnd('');
		const f = fieldOptions.find((x) => x.key === key);
		if (f?.type === 'currency') {
			setFilterCurrency(lockedCurrency || resolvedDefaultCurrency);
		} else {
			setFilterCurrency('');
		}
	}
	function onChipClick(clause, idx) {
		const f = fieldOptions.find((x) => x.key === clause.fieldKey);
		if (f?.type === 'date') {
			const p = parseDateClauseToState(clause.value);
			setEditingIndex(idx); setSelectedField(clause.fieldKey); setInputValue(clause.value); setDatePreset(p.preset); setDateStart(p.date); setDateEnd(p.dateEnd); onToggle(true); return;
		}
		if (f?.type === 'currency') {
			const p = parseAmountClauseToState(clause.value);
			setEditingIndex(idx); setSelectedField(clause.fieldKey); setInputValue(clause.value);
			setAmountOp(p.op); setAmount(p.amount); setAmountEnd(p.amountEnd); setFilterCurrency(p.currency); onToggle(true); return;
		}
		base.handleChipClick(clause, idx);
	}
	function onAddNew() { base.handleAddNew(); setDatePreset(''); setDateStart(''); setDateEnd(''); setAmountOp(''); setAmount(''); setAmountEnd(''); setFilterCurrency(''); }
	function onCancel() { base.handleCancel(); setDatePreset(''); setDateStart(''); setDateEnd(''); setAmountOp(''); setAmount(''); setAmountEnd(''); setFilterCurrency(''); }
	function applyDate() {
		const built = buildDateClauseValue(datePreset, dateStart, dateEnd);
		if (!selectedField || !built) { return; }
		const next = editingIndex !== null ? clauses.map((c, i) => i === editingIndex ? { fieldKey: selectedField, value: built } : c) : [...clauses, { fieldKey: selectedField, value: built }];
		setClauses(next); onFilterChange(next); setSelectedField(''); setInputValue(''); setDatePreset(''); setDateStart(''); setDateEnd(''); setEditingIndex(null); onToggle(false);
	}
	function applyAmount() {
		const cur = lockedCurrency || filterCurrency || resolvedDefaultCurrency;
		const built = buildAmountClauseValue(amountOp, amount, amountEnd, cur);
		if (!selectedField || !built) { return; }
		const next = editingIndex !== null ? clauses.map((c, i) => i === editingIndex ? { fieldKey: selectedField, value: built } : c) : [...clauses, { fieldKey: selectedField, value: built }];
		setClauses(next); onFilterChange(next); setSelectedField(''); setInputValue(''); setAmountOp(''); setAmount(''); setAmountEnd(''); setFilterCurrency(''); setEditingIndex(null); onToggle(false);
	}
	const needs = datePresetNeedsPicker(datePreset);
	const needsOne = needs === 1;
	const needsTwo = needs === 2;
	const isBetween = amountOp === 'between';
	const canApplyCurrency = Boolean(selectedField && amountOp && (lockedCurrency || filterCurrency || resolvedDefaultCurrency) && amount && (isBetween ? Boolean(amountEnd) : true));
	const canApply = isCurrency ? canApplyCurrency : isDate
		? Boolean(selectedField && datePreset && (needs === 0 ? true : needs === 1 ? Boolean(dateStart) : Boolean(dateStart) && Boolean(dateEnd) && dateStart <= dateEnd))
		: Boolean(selectedField && inputValue);
	const apply = isCurrency ? applyAmount : isDate ? applyDate : base.applyFilter;
	return (
		<div className="filter-bar">
			{clauses.length > 0 && (
				<div className="filter-bar-chips">
					{clauses.map((c, i) => {
						const field = filterFields.find((f) => f.key === c.fieldKey) || (c.fieldKey === '*' ? { label: t('filter.allFields'), key: '*' } : null);
						const label = field?.label ?? c.fieldKey;
						const displayValue = getChipDisplayValue(field, c.value, t, dateFormat);
						return (<Pill key={`${c.fieldKey}:${c.value}:${i}`} label={`${label}:`} onClick={() => onChipClick(c, i)} onRemove={() => base.removeClause(c)} removeLabel={t('aria.removeFilter', { label, value: displayValue })}>{displayValue}</Pill>);
					})}
					<Button size="sm" variant="ghost" className="filter-add-chip" onClick={onAddNew} aria-label={t('aria.addFilter')}>+</Button>
					<span className="filter-chips-spacer" />
					<Button variant="ghost" size="sm" className="filter-clear-all" onClick={base.clearAll}>{t('btn.clearAll')}</Button>
				</div>
			)}
			{open && (
				<div className="filter-bar-panel">
					<Combobox id="filter-field" ariaLabel={t('aria.filterField')} className="filter-field-select" value={selectedField} options={fieldOptions.map((f) => ({ value: f.key, label: f.label }))} onChange={(v) => onFieldChange(v)} selectOnly placeholder={t('field.select')} clearable emptyLabel={t('field.select')} />
					{selectedField && isCurrency ? (
						<>
							<Combobox key={`filter-op-${selectedField}`} id="filter-operator" ariaLabel={t('aria.filterOperator')} inputRef={amountOpRef} pickerRef={amountOpPickerRef} className="filter-value-select" value={amountOp} options={amountOperatorOptions} onChange={(v) => setAmountOp(v)} selectOnly placeholder={t('field.select')} />
							<input ref={amountInputRef} type="text" inputMode="decimal" className="filter-amount-input" value={amount} onChange={(e) => setAmount(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { apply(); } }} placeholder={t('dialog.filterAmount')} aria-label={t('aria.filterAmount')} />
							{isBetween && (<input type="text" inputMode="decimal" className="filter-amount-input" value={amountEnd} onChange={(e) => setAmountEnd(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { apply(); } }} placeholder={t('dialog.filterAmountEnd')} aria-label={t('aria.filterAmountEnd')} />)}
							<Combobox id="filter-currency" ariaLabel={t('aria.filterCurrency')} className="filter-value-select" value={effectiveCurrency} options={currencyOptions} onChange={(v) => { if (!lockedCurrency) { setFilterCurrency(v); } }} selectOnly placeholder={t('field.select')} disabled={Boolean(lockedCurrency)} />
						</>
					) : selectedField && isDate ? (
						<>
							<Combobox key={`filter-preset-${selectedField}`} id="filter-preset" ariaLabel={t('aria.filterPreset')} inputRef={datePresetRef} pickerRef={datePresetPickerRef} className="filter-value-select" value={datePreset} options={datePresetOptions} onChange={(v) => { setDatePreset(v); if (v !== 'from' && v !== 'to' && v !== 'between') { setDateStart(''); setDateEnd(''); } else if (v !== 'between') { setDateEnd(''); } }} selectOnly placeholder={t('field.select')} />
							{needsOne && (<input ref={dateStartRef} type="date" className="filter-date-input" value={dateStart} onChange={(e) => setDateStart(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { apply(); } }} aria-label={t('aria.filterDate')} />)}
							{needsTwo && (<><input ref={dateStartRef} type="date" className="filter-date-input" value={dateStart} onChange={(e) => setDateStart(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { apply(); } }} aria-label={t('aria.filterDateStart')} /><input ref={dateEndRef} type="date" className="filter-date-input" value={dateEnd} onChange={(e) => setDateEnd(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { apply(); } }} aria-label={t('aria.filterDateEnd')} /></>)}
						</>
					) : selectedField && isSelect ? (
						<Combobox key={`filter-value-${selectedField}`} id="filter-value" ariaLabel={t('aria.filterValue')} inputRef={valueSelectRef} pickerRef={valuePickerRef} className="filter-value-select" value={inputValue} options={selectOptions} onChange={(v) => setInputValue(v)} selectOnly defaultOpen placeholder={t('field.select')} />
					) : (
						<input ref={valueInputRef} type="text" className="filter-value-input" value={inputValue} onChange={(e) => setInputValue(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') {apply();} }} placeholder={t('dialog.filterValue')} disabled={!selectedField} aria-label={t('aria.filterValue')} />
					)}
					<Button variant="primary" size="sm" onClick={apply} disabled={!canApply}>{t('btn.apply')}</Button>
					<Button size="sm" onClick={onCancel}>{t('btn.cancel')}</Button>
				</div>
			)}
		</div>
	);
}
